/**
 * Pure utility functions for parsing order bulk-upload error responses.
 * Extracted from useBulkImport to keep the hook under 300 lines.
 */

import { extractValue } from '@/util/formatting';
import { getEntityFallbackValue } from '@/services/entity-tree';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ErrorCellField {
  value: string | string[];
  isActive: boolean;
  errorMessage?: string;
  isOptionalField?: boolean;
}

type FieldMapping = [
  field: ErrorCellField,
  patterns: string[],
  message: string,
  trackActive?: boolean
];

export interface GetFailedTableDataResult {
  result: Record<string, unknown>[];
  failedCount: number;
  successCount: number;
}

// ---------------------------------------------------------------------------
// Field builders
// ---------------------------------------------------------------------------

function buildField(
  rawValue: string | null,
  isOptionalField = false
): ErrorCellField {
  return {
    value: extractValue(rawValue),
    isActive: false,
    isOptionalField
  };
}

function buildServicesField(
  item: Record<string, unknown>
): ErrorCellField {
  if (Array.isArray(item?.services)) {
    return {
      value: (item.services as string[]).map(
        (s) => extractValue(s) as string
      ),
      isActive: false
    };
  }
  return {
    value: extractValue(item.services as string | null),
    isActive: false
  };
}

// ---------------------------------------------------------------------------
// Error-reason field mappings
// ---------------------------------------------------------------------------

function buildOrderFieldMappings(fields: {
  services: ErrorCellField;
  address: ErrorCellField;
  propertyType: ErrorCellField;
  unitNumber: ErrorCellField;
  city: ErrorCellField;
  state: ErrorCellField;
  zipCode: ErrorCellField;
  onsiteContactName: ErrorCellField;
  onsiteContactPhone: ErrorCellField;
  accessType: ErrorCellField;
  lockboxLocation: ErrorCellField;
  accessCode: ErrorCellField;
  rentlyRegistrationPin: ErrorCellField;
  entity: ErrorCellField;
  orderNotes: ErrorCellField;
  gateCode: ErrorCellField;
  firstAvailableDate: ErrorCellField;
  propertyId: ErrorCellField;
  previousOrderId: ErrorCellField;
  beds: ErrorCellField;
  baths: ErrorCellField;
  squareFeet: ErrorCellField;
}): FieldMapping[] {
  return [
    [fields.services, ['services', 'Service(s)'], 'Not a valid service.', true],
    [fields.address, ['Address', 'address1'], 'Not a valid address.', true],
    [fields.propertyType, ['Property Type'], 'Not a valid property Type.', true],
    [fields.city, ['City'], 'Not a valid city.', true],
    [fields.state, ['State', 'state'], 'Not a valid state.', true],
    [fields.zipCode, ['zip code', 'Zip', 'zip', 'zipcode', 'Zip Code'], 'Not a valid zip code.', true],
    [fields.onsiteContactName, ['On-Site Contact Name'], 'Not a valid on site contact name.', true],
    [fields.onsiteContactPhone, ['On-Site Contact Phone'], 'Not a valid on site contact phone.', true],
    [fields.accessType, ['Access Type'], 'Not a valid access type.', true],
    [fields.lockboxLocation, ['Lockbox Location'], 'Not a valid Lockbox location.', true],
    [fields.accessCode, ['accessCode', 'Access Code'], 'Not a valid access code.', true],
    [fields.rentlyRegistrationPin, ['rentlyRegistrationPin', 'Rently Registration Pin'], 'Not a valid rently registration pin.', true],
    [fields.entity, ['Entity ID', 'Entity'], 'Not a valid entity.', true],
    [fields.propertyId, ['Property Id', 'Property ID'], 'Limit 50 characters'],
    // DV-2188: generic fallback first; specific phrase patterns after it win (last match sets the message).
    [fields.previousOrderId, ['Previous Order ID'], 'Enter a valid Previous Order ID.'],
    [fields.previousOrderId, ['required when ordering an Image Edit'], 'Previous Order ID is required for AI Virtual Staging (Image Edit) services.'],
    [fields.previousOrderId, ['only valid for Image Edit'], 'Previous Order ID applies only to AI Virtual Staging (Image Edit) services — remove it for property services.'],
    [fields.previousOrderId, ['must be a numeric order ID'], 'Enter a valid numeric Previous Order ID.'],
    [fields.services, ['cannot mix Image Edit'], "AI Virtual Staging (Image Edit) can't be combined with property services — order them separately.", true],
    [fields.orderNotes, ['orderNotes', 'Order Notes'], 'Not a valid notes'],
    [fields.gateCode, ['gateCode', 'Gate code', 'Gate Code'], 'Not a valid gate code'],
    [fields.firstAvailableDate, ['firstAvailableDate', 'Schedule Later Date', 'First Available Date', 'First available date'], 'Enter a valid date'],
    [fields.beds, ['Bedrooms', 'Beds'], 'Enter a valid number'],
    [fields.baths, ['Bathrooms', 'Baths'], 'Enter a valid number'],
    [fields.squareFeet, ['Square Feet'], 'Enter a valid number']
  ];
}

function applyErrorReasons(
  reasons: string[],
  mappings: FieldMapping[],
  catchAllFields: ErrorCellField[]
): void {
  let anyMatched = false;

  for (const reason of reasons) {
    let reasonMatched = false;
    for (const [field, patterns, message, trackActive] of mappings) {
      if (patterns.some((p) => reason?.includes(p))) {
        field.isActive = true;
        field.errorMessage = message;
        if (trackActive) anyMatched = true;
        reasonMatched = true;
      }
    }
    if (
      !reasonMatched &&
      reason?.includes('Something went wrong, please contact admin')
    ) {
      if (!anyMatched) {
        for (const f of catchAllFields) {
          f.isActive = true;
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Main exported function
// ---------------------------------------------------------------------------

export function getFailedTableData(
  tableDataArr: Record<string, unknown>[],
  _isProperty: boolean
): GetFailedTableDataResult {
  const result: Record<string, unknown>[] = [];
  let failedCount = 0;
  let successCount = 0;

  tableDataArr.forEach((item: Record<string, unknown>, index: number) => {
    const services = buildServicesField(item);
    const address = buildField(item.address as string | null);
    const propertyType = buildField(item.propertyType as string | null);
    const unitNumber = buildField(item.unitNumber as string | null);
    const city = buildField(item.city as string | null);
    const state = buildField(item.state as string | null);
    const zipCode = buildField(item.zip as string | null);
    const onsiteContactName = buildField(item.onsiteContactName as string | null);
    const onsiteContactPhone = buildField(item.onsiteContactPhone as string | null);
    const accessType = buildField(item.accessType as string | null);
    const lockboxLocation = buildField(item.lockboxLocation as string | null);
    const accessCode = buildField(item.accessCode as string | null);
    const rentlyRegistrationPin = item.rentlyRegistrationPin
      ? buildField(item.rentlyRegistrationPin as string | null)
      : { value: '-', isActive: false };
    const entityRaw = extractValue(item.entity as string | null);
    const entity: ErrorCellField = {
      value:
        entityRaw === '-' || entityRaw === ''
          ? getEntityFallbackValue()
          : (entityRaw as string),
      isActive: false,
    };
    const orderNotes = buildField(item.orderNotes as string | null, true);
    const gateCode = buildField(item.gateCode as string | null, true);
    const firstAvailableDate = buildField(item.firstAvailableDate as string | null, true);
    const propertyId = buildField(item.propertyId as string | null, true);
    const previousOrderId = buildField(item.previousOrderId as string | null, true);
    const beds = buildField(item.beds as string | null, true);
    const baths = buildField(item.baths as string | null, true);
    const squareFeet = buildField(item.squareFeet as string | null, true);

    if (item.errorReasons) {
      failedCount += 1;
      const fields = {
        services, address, propertyType, unitNumber, city, state,
        zipCode, onsiteContactName, onsiteContactPhone, accessType,
        lockboxLocation, accessCode, rentlyRegistrationPin, entity,
        orderNotes, gateCode, firstAvailableDate, propertyId,
        previousOrderId, beds, baths, squareFeet
      };
      const mappings = buildOrderFieldMappings(fields);
      const catchAllFields = [
        services, address, propertyType, city, state, zipCode,
        unitNumber, onsiteContactName, onsiteContactPhone,
        accessType, lockboxLocation, accessCode, rentlyRegistrationPin,
        entity
      ];
      for (const [f, , msg] of mappings) {
        if (catchAllFields.includes(f) && !f.errorMessage) {
          f.errorMessage = msg;
        }
      }
      applyErrorReasons(item.errorReasons as string[], mappings, catchAllFields);
    } else {
      successCount += 1;
    }

    result.push({
      fileId: item.fileId, status: item.status, orderId: item.orderId,
      propertyPrice: item.propertyPrice, orderSource: item.orderSource,
      rowNumber: item.rowNumber, id: index + 1,
      isOnsiteAppointment: item.isOnsiteAppointment,
      isAccessNeeded: item.isAccessNeeded,
      services, propertyType, address, unitNumber, city, state,
      zipCode, onsiteContactName, onsiteContactPhone, accessType,
      lockboxLocation, accessCode, rentlyRegistrationPin, entity,
      orderNotes, gateCode, firstAvailableDate, propertyId,
      previousOrderId, beds, baths, squareFeet
    });
  });

  return { result, failedCount, successCount };
}
